import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { Badge } from '../components/ui/badge';
import { MapPin, Truck, Package, Loader2, X, Calendar } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRANCE_CENTER = [46.603354, 1.888334];

const STATUT_COLOR = {
    a_envoyer: '#f59e0b',
    envoye: '#3b82f6',
    relance_1: '#f97316',
    relance_2: '#f97316',
    relance_3: '#f97316',
    relance_whatsapp: '#10b981',
    interesse: '#6366f1',
    decision_pas_prise: '#64748b',
    accepte: '#C9A962',
    nouveau: '#3b82f6',
    programmee: '#10b981',
    stocker: '#a855f7',
    terminee: '#0f766e',
};

const STATUT_LABEL = {
    a_envoyer: 'À envoyer',
    envoye: 'Envoyé',
    relance_1: 'Relance 1',
    relance_2: 'Relance 2',
    relance_3: 'Relance 3',
    relance_whatsapp: 'Relance WhatsApp',
    interesse: 'Intéressé',
    decision_pas_prise: 'Décision pas prise',
    accepte: 'Accepté',
    nouveau: 'Nouveau',
    programmee: 'Programmée',
    stocker: 'Stocker',
    terminee: 'Terminée',
};

const statutColor = (s) => STATUT_COLOR[s] || '#64748b';
const statutLabel = (s) => STATUT_LABEL[s] || s || '—';

// Haversine distance in km between two lat/lon points
function haversineKm(lat1, lon1, lat2, lon2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
}

function MapAutoFit({ searchPoint, markers }) {
    const map = useMap();
    useEffect(() => {
        if (!searchPoint) return;
        // Build bounds from searched point + all visible markers
        const coords = [[searchPoint.lat, searchPoint.lon]];
        markers.forEach((m) => coords.push([m.lat, m.lon]));
        if (coords.length === 1) {
            map.flyTo(coords[0], 6, { duration: 1.2 });
            return;
        }
        // Fit all points with some padding; cap max zoom so we see France
        map.flyToBounds(coords, { padding: [60, 60], maxZoom: 7, duration: 1.2 });
    }, [searchPoint?.lat, searchPoint?.lon, markers.length]);
    return null;
}

export default function CarteGroupage() {
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [searchPoint, setSearchPoint] = useState(null); // { lat, lon, label }
    const [filterDirection, setFilterDirection] = useState('all');
    const [datePreset, setDatePreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const toISO = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const j = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${j}`;
    };
    const applyDatePreset = (p) => {
        setDatePreset(p);
        const today = new Date(); const iso = toISO(today);
        if (p === 'all') { setDateFrom(''); setDateTo(''); return; }
        if (p === '7d') { const d = new Date(); d.setDate(d.getDate() - 6); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === '30d') { const d = new Date(); d.setDate(d.getDate() - 29); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === 'month') { const f = new Date(today.getFullYear(), today.getMonth(), 1); setDateFrom(toISO(f)); setDateTo(iso); return; }
        if (p === 'next30') { const d = new Date(); d.setDate(d.getDate() + 30); setDateFrom(iso); setDateTo(toISO(d)); return; }
    };
    const clearDates = () => { setDatePreset('all'); setDateFrom(''); setDateTo(''); };

    const fetchPoints = (params) => {
        setLoading(true);
        axios.get(`${API_URL}/api/carte-groupage`, { params, withCredentials: true })
            .then(r => setPoints(r.data || []))
            .catch(() => toast.error('Erreur chargement carte'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        const params = { min_km: 200 };
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        fetchPoints(params);
    }, [dateFrom, dateTo]);

    const handleSelectAddress = (addr) => {
        if (!addr?.lat || !addr?.lon) {
            toast.error('Adresse sans coordonnées');
            return;
        }
        setSearchPoint({
            lat: parseFloat(addr.lat),
            lon: parseFloat(addr.lon),
            label: addr.adresse || addr.display || '',
        });
    };

    const clearSearch = () => {
        setSearchPoint(null);
        setSearchValue('');
    };

    // Augment points with distance-from-search when a search is active
    const decoratedPoints = useMemo(() => {
        if (!searchPoint) return points;
        return points.map((p) => ({
            ...p,
            distance_from_search: haversineKm(searchPoint.lat, searchPoint.lon, p.lat, p.lon),
        }));
    }, [points, searchPoint]);

    const filtered = useMemo(() => {
        if (filterDirection === 'all') return decoratedPoints;
        return decoratedPoints.filter(p => p.direction === filterDirection);
    }, [decoratedPoints, filterDirection]);

    const stats = useMemo(() => ({
        total: points.length,
        depart: points.filter(p => p.direction === 'depart').length,
        arrivee: points.filter(p => p.direction === 'arrivee').length,
    }), [points]);

    return (
        <Layout>
            <div className="space-y-2" data-testid="carte-groupage-page">
                {/* Header with search + stats — compact */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-2 flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex items-center gap-2 shrink-0">
                        <MapPin className="w-4 h-4 text-[#C9A962]" />
                        <h1 className="text-sm font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Carte groupage</h1>
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] px-1.5 py-0">&gt; 200 km Paris</Badge>
                    </div>

                    <div className="flex-1 flex items-center gap-2 md:ml-2 relative">
                        <div className="flex-1">
                            <AddressAutocomplete
                                value={searchValue}
                                onChange={setSearchValue}
                                onSelect={handleSelectAddress}
                                placeholder="Rechercher une adresse…"
                                testId="cg-search-input"
                            />
                        </div>
                        {searchPoint && (
                            <button
                                onClick={clearSearch}
                                className="px-2 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-[11px] text-gray-600 font-semibold flex items-center gap-1 transition-colors shrink-0"
                                data-testid="cg-clear-search"
                                title="Effacer la recherche"
                            >
                                <X className="w-3 h-3" /> Effacer
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 text-[11px] text-gray-600 shrink-0">
                        <button
                            onClick={() => setFilterDirection('all')}
                            className={`px-2.5 py-1 rounded-full border font-semibold transition-all ${filterDirection === 'all' ? 'bg-[#1B365D] text-white border-[#1B365D]' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                            data-testid="cg-filter-all"
                        >Tous ({stats.total})</button>
                        <button
                            onClick={() => setFilterDirection('depart')}
                            className={`px-2.5 py-1 rounded-full border font-semibold transition-all flex items-center gap-1 ${filterDirection === 'depart' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white border-emerald-200 text-emerald-700 hover:border-emerald-300'}`}
                            data-testid="cg-filter-depart"
                        >
                            <Truck className="w-3 h-3" /> Charg. ({stats.depart})
                        </button>
                        <button
                            onClick={() => setFilterDirection('arrivee')}
                            className={`px-2.5 py-1 rounded-full border font-semibold transition-all flex items-center gap-1 ${filterDirection === 'arrivee' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-blue-200 text-blue-700 hover:border-blue-300'}`}
                            data-testid="cg-filter-arrivee"
                        >
                            <Package className="w-3 h-3" /> Décharg. ({stats.arrivee})
                        </button>
                    </div>
                </div>

                {/* Filtre période — compact single line */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-1.5 flex items-center gap-2 flex-wrap" data-testid="cg-date-filters">
                    <Calendar className="w-3.5 h-3.5 text-[#1B365D] shrink-0" />
                    <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                        {[
                            { v: 'all', label: 'Tout' },
                            { v: '7d', label: '7j' },
                            { v: '30d', label: '30j' },
                            { v: 'month', label: 'Ce mois' },
                            { v: 'next30', label: '+30j' },
                            { v: 'custom', label: 'Perso.' },
                        ].map(p => (
                            <button key={p.v} onClick={() => applyDatePreset(p.v)}
                                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${datePreset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                data-testid={`cg-date-preset-${p.v}`}
                            >{p.label}</button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1">
                        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="cg-date-from" />
                        <span className="text-gray-400 text-[10px]">→</span>
                        <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="cg-date-to" />
                        {(dateFrom || dateTo) && (
                            <button onClick={clearDates} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-testid="cg-date-clear">
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                    <div className="ml-auto text-[10px] text-gray-500">
                        Source : <span className="font-semibold text-[#1B365D]">prospects avec devis</span>
                    </div>
                </div>

                {/* Map — full visibility, legend overlay at bottom */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden relative" style={{ height: 'calc(100vh - 165px)', minHeight: '500px' }}>
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-[500]">
                            <Loader2 className="w-8 h-8 animate-spin text-[#1B365D]" />
                        </div>
                    ) : null}
                    <MapContainer center={FRANCE_CENTER} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                        <TileLayer
                            attribution='&copy; OpenStreetMap'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapAutoFit searchPoint={searchPoint} markers={filtered} />

                        {filtered.map((p, i) => {
                            const isDevis = p.entity_type === 'devis';
                            const directionColor = p.direction === 'depart' ? '#10b981' : '#3b82f6';
                            const color = isDevis ? statutColor(p.statut) : directionColor;
                            return (
                                <CircleMarker
                                    key={`${p.entity_type}-${p.entity_id}-${p.direction}-${i}`}
                                    center={[p.lat, p.lon]}
                                    radius={8}
                                    pathOptions={{ color, fillColor: color, fillOpacity: 0.8, weight: 2 }}
                                >
                                    <Popup>
                                        <div className="text-sm space-y-1 min-w-[220px]">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                                    style={{ backgroundColor: color }}
                                                />
                                                <strong className="text-[#1B365D]">{p.nom}</strong>
                                            </div>
                                            <div className="text-xs text-gray-500 uppercase font-semibold">
                                                {p.direction === 'depart' ? 'Chargement' : 'Déchargement'}
                                                {isDevis && <span className="ml-2 text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">Devis · {statutLabel(p.statut)}</span>}
                                                {!isDevis && p.entity_type === 'prospect' && <span className="ml-2 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Prospect</span>}
                                                {!isDevis && p.entity_type === 'client' && <span className="ml-2 text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">Client · {statutLabel(p.statut)}</span>}
                                            </div>
                                            {p.telephone && <div className="text-xs text-gray-700">📞 {p.telephone}</div>}
                                            <div className="text-xs text-gray-700">📍 {p.adresse}</div>
                                            {p.date && <div className="text-xs text-gray-700">📅 {p.date}</div>}
                                            <div className="text-xs text-gray-700">📦 Volume : <span className="font-semibold">{p.volume ? `${p.volume} m³` : '—'}</span></div>
                                            <div className="text-[11px] pt-1 border-t mt-1 flex items-center justify-between gap-2">
                                                {searchPoint ? (
                                                    <>
                                                        <span className="text-red-600 font-bold">↔ {Math.round(p.distance_from_search)} km</span>
                                                        <span className="text-gray-400 text-[10px]">du point recherché</span>
                                                    </>
                                                ) : (
                                                    <span className="text-gray-400">{p.distance_km} km de Paris</span>
                                                )}
                                            </div>
                                        </div>
                                    </Popup>
                                </CircleMarker>
                            );
                        })}

                        {searchPoint && (
                            <CircleMarker
                                center={[searchPoint.lat, searchPoint.lon]}
                                radius={10}
                                pathOptions={{ color: '#ef4444', fillColor: '#fee2e2', fillOpacity: 0.8, weight: 3 }}
                            >
                                <Popup>
                                    <div className="text-sm">
                                        <strong className="text-red-600">Point recherché</strong>
                                        <div className="text-xs text-gray-700 mt-1">{searchPoint.label}</div>
                                    </div>
                                </Popup>
                            </CircleMarker>
                        )}
                    </MapContainer>

                    {/* Légende — overlay en bas de la carte */}
                    <div
                        className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[400] bg-white/95 backdrop-blur-sm border border-gray-200 shadow-md rounded-full px-3 py-1 flex items-center gap-3 text-[10px] whitespace-nowrap"
                        data-testid="cg-legend"
                    >
                        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />Chargement</span>
                        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-blue-500" />Déchargement</span>
                        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full border-2 border-red-500 bg-red-100" />Recherche</span>
                        {searchPoint && (
                            <span className="ml-1 pl-2 border-l border-gray-200 text-[#1B365D] font-semibold flex items-center gap-1" data-testid="cg-search-banner">
                                <MapPin className="w-3 h-3 text-red-500" />
                                <span className="text-gray-700 font-normal truncate max-w-[200px]">{searchPoint.label}</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
